
import { computed, defineComponent, onMounted, PropType, reactive } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// validations
import { required } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import {
    AdditionalServicesFTLI,
    AdditionalServicesTypeT,
    FTLCreateAdditionalServicePayloadT,
} from '@/core/types/additional_services.types'
import { additionalServices } from '@/core/realisations/additional_services.realisation'
import { FTLBidWaypointT } from '@/core/types/ftl_bids.types'
import useNotifications from '@/common/composable/useNotifications'
import { FTL } from '@/core/realisations/FTL.realisation'
import { FullFTLOrderSenderT, StockDeliveryNoteT } from '@/core/types/orders.types'

export default defineComponent({
    components: {
        Modal,
        TheSelect,
    },
    emits: ['submitted', 'cancel'],
    props: {
        isLoading: {
            type: Boolean,
            default: false,
        },
        //
        waypoint: {
            type: Object as PropType<FTLBidWaypointT>,
            default: null,
        },
        //
        orderId: {
            type: Number,
            default: null,
        },
        sender: {
            type: Object as PropType<FullFTLOrderSenderT>,
            default: null,
        },
        senderServices: {
            type: Array as PropType<AdditionalServicesFTLI[]>,
            default: () => [],
        },
        //
        stock: {
            type: Object as PropType<StockDeliveryNoteT>,
            default: null,
        },
    },
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            typeIds: [] as number[],
        })

        const data_state = reactive({
            additional_services: new DataState<AdditionalServicesTypeT>({}),
        })

        const state_form$ = useVuelidate({ typeIds: { required } }, state)

        const emitCancel = () => {
            emit('cancel')
        }

        const getPayload = () => {
            const payload: FTLCreateAdditionalServicePayloadT = {
                typeIds: state.typeIds,
            }

            if (props.stock) {
                payload.stockDeliveryNoteId = props.stock.id
            } else if (props.waypoint) {
                payload.waypointId = props.waypoint.id
            } else if (props.sender) {
                payload.orderId = props.orderId
            }

            return payload
        }

        const emitSubmitted = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_loading = true

                await FTL.createAdditionalService(getPayload())

                notifications.notify({
                    title: 'Доп услуги успешно добавлены!',
                    type: 'success',
                })
                emit('submitted')
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При добавлении доп. услуги произошла ошбика',
                })
            } finally {
                state.is_loading = false
            }
        }

        const setServices = (data: AdditionalServicesTypeT[]) => {
            if (props.stock) {
                const stockServicesIds = props.stock.additional_service.map((s) => s.type_id)
                data_state.additional_services.list = data.filter(
                    (service) => !stockServicesIds.includes(service.id)
                )
            } else if (props.sender) {
                const senderServicesIds = props.senderServices.map((s) => s.type_id)
                data_state.additional_services.list = data.filter(
                    (service) => !senderServicesIds.includes(service.id)
                )
            } else if (props.waypoint) {
                state.typeIds = props.waypoint.additional_service.map((service) => service.type_id)
            }
        }

        const loadAdditionalServices = async () => {
            data_state.additional_services.pagination.current_page = 1

            try {
                data_state.additional_services.is_loading = true

                const response = await additionalServices.getList()
                data_state.additional_services.list = response.data

                setServices(response.data)

                data_state.additional_services.pagination.total_pages = 1
                data_state.additional_services.is_failed = false
                data_state.additional_services.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.additional_services.failure.catchError(error)

                if (!data_state.additional_services.failure.error_state.is_cancel) {
                    data_state.additional_services.is_failed = true
                }
            } finally {
                if (!data_state.additional_services.failure.error_state.is_cancel) {
                    data_state.additional_services.is_loading = false
                }
            }
        }

        //

        const loading = computed(() => props.isLoading || state.is_loading)

        //

        onMounted(loadAdditionalServices)

        //

        return {
            state,
            state_form$,
            data_state,
            //
            emitCancel,
            emitSubmitted,
            loadAdditionalServices,
            //
            loading,
        }
    },
})
